import React from 'react';
import { Target, Clock } from 'lucide-react';
import { formatKickoff, formatCountdown } from '../../lib/formatDate';

/**
 * "Make your pick" — the one thing on a league page that has a deadline attached.
 *
 * Until now the way in was a button called "Make Your Pick" sitting inside a
 * tournament row, below the table, the members, and however many past cycles the
 * league had run. It was the most urgent thing on the page and the furthest down it,
 * and a missed pick eliminates you — so it now leads the page instead, and looks like
 * the only button worth pressing.
 *
 * Rendered from PICK_NEEDED pending actions rather than worked out here. The server
 * already knows whether the week is open and whether a pick is in; deriving that in the
 * browser would mean a request per game and a second copy of the rule to keep right.
 *
 * Nothing outstanding renders nothing at all. This is a call to action, not a status
 * board, and a green banner that is usually empty teaches people to skip that space.
 */
function PickCallout({ actions = [], onOpenGame, leagueName }) {
  if (actions.length === 0) return null;

  const many = actions.length > 1;

  return (
    <div className="pick-callout" aria-label="Picks needed">
      <div className="pick-callout-head">
        <span className="pick-callout-icon">
          <Target size={20} />
        </span>
        <div>
          <h2 className="pick-callout-title">
            {many ? `${actions.length} picks needed` : 'Your pick is needed'}
          </h2>
          <p className="pick-callout-sub">
            {many
              ? 'Miss a deadline and you are out of that game.'
              : 'Pick a winner before the deadline or you are out.'}
          </p>
        </div>
      </div>

      <div className="pick-callout-list">
        {actions.map((action) => (
          <button
            key={action.gameId}
            type="button"
            className="pick-callout-row"
            onClick={() => onOpenGame(action.gameId, 'picks')}
          >
            <span className="pick-callout-where">
              <span className="pick-callout-game">
                {action.gameName || leagueName || 'Your game'}
              </span>
              <span className="pick-callout-when">
                {action.gameWeek ? `Week ${action.gameWeek}` : 'This week'}
                {action.deadline && (
                  <>
                    {' · '}
                    <Clock size={12} />
                    {/* Both: the countdown answers "have I got time", the date and time
                        answer "when exactly", and people planning a weekend want the second. */}
                    <span className="pick-callout-countdown">
                      {formatCountdown(action.deadline)}
                    </span>
                    {` (${formatKickoff(action.deadline)})`}
                  </>
                )}
              </span>
            </span>
            <span className="pick-callout-cta">Make your pick</span>
          </button>
        ))}
      </div>
    </div>
  );
}

export default PickCallout;
